import React, { useState, useEffect } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { ClassificationExplainabilityTree, type ExplainabilityData } from './ClassificationExplainabilityTree';
import type { Id } from '../../../convex/_generated/dataModel';
import type { ClassificationResult } from '../types/classificationResult';
import { Alert, Badge, Stack } from '@mantine/core';
import { normalizeConfidenceScore } from '../utils/confidence';

type UniversalReasoningData = NonNullable<ExplainabilityData['universalReasoning']>;
type ValidationAuditData = NonNullable<ExplainabilityData['validationAudit']>;

function normalizeRoutingSource(value: unknown): UniversalReasoningData['routingSource'] {
  return value === 'FAMILY' || value === 'CHAPTER_HINT' || value === 'KEYWORD' || value === 'NONE'
    ? value
    : 'NONE';
}

function normalizeDecisionStatus(value: unknown): UniversalReasoningData['decisionStatus'] {
  return value === 'READY' || value === 'NEEDS_INFO' || value === 'BLOCKED'
    ? value
    : 'BLOCKED';
}

function normalizeAttributeStatus(value: unknown): 'PRESENT' | 'ABSENT' | 'AMBIGUOUS' {
  return value === 'PRESENT' || value === 'ABSENT' || value === 'AMBIGUOUS'
    ? value
    : 'AMBIGUOUS';
}

function normalizeUniversalReasoning(raw: unknown): UniversalReasoningData | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  const value = raw as Record<string, unknown>;
  const extractedAttributes = Array.isArray(value.extractedAttributes)
    ? value.extractedAttributes
        .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
        .map((entry) => ({
          name: typeof entry.name === 'string' ? entry.name : 'Unknown',
          value: typeof entry.value === 'string' ? entry.value : null,
          status: normalizeAttributeStatus(entry.status),
        }))
    : [];
  const auditTrail = Array.isArray(value.auditTrail)
    ? value.auditTrail
        .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
        .map((entry) => ({
          stage: typeof entry.stage === 'string' ? entry.stage : 'UNKNOWN',
          timestamp: typeof entry.timestamp === 'string' ? entry.timestamp : '',
          action: typeof entry.action === 'string' ? entry.action : '',
        }))
    : [];

  return {
    productFamily: typeof value.productFamily === 'string' ? value.productFamily : null,
    familyConfidence: typeof value.familyConfidence === 'number' ? value.familyConfidence : 0,
    domainPackId: typeof value.domainPackId === 'string' ? value.domainPackId : null,
    routingSource: normalizeRoutingSource(value.routingSource),
    extractedAttributes,
    foundMandatory: typeof value.foundMandatory === 'number' ? value.foundMandatory : 0,
    missingMandatory: typeof value.missingMandatory === 'number' ? value.missingMandatory : 0,
    targetedClarifications: Array.isArray(value.targetedClarifications)
      ? value.targetedClarifications.filter((entry): entry is string => typeof entry === 'string')
      : [],
    decisionStatus: normalizeDecisionStatus(value.decisionStatus),
    decisionReason: typeof value.decisionReason === 'string' ? value.decisionReason : '',
    maxAllowedDepth: String(value.maxAllowedDepth ?? ''),
    depthEnforced: Boolean(value.depthEnforced),
    summaryBs: typeof value.summaryBs === 'string' ? value.summaryBs : '',
    auditTrail,
    engineVersion: typeof value.engineVersion === 'string' ? value.engineVersion : 'unknown',
  };
}

function buildValidationAuditData(data: Partial<ClassificationResult>): ValidationAuditData | undefined {
  const gate = data.validatorGate;
  const validation = data.validation;
  if (!gate && !validation) return undefined;

  return {
    status: data.status || "UNKNOWN",
    gateName: gate?.gate_name,
    finalStatus: gate?.final_status,
    reasonCodes: gate?.reason_codes ?? [],
    blockingReasons: [
      ...(gate?.blocking_reasons ?? []),
      ...(gate?.final_eligibility?.blocking_reasons ?? []),
    ],
    warnings: [
      ...(gate?.warnings ?? []),
      ...(gate?.final_eligibility?.warnings ?? []),
    ],
    dataQualityFallback: Boolean(gate?.data_quality_fallback),
    legalChain: gate?.legal_chain
      ? {
          status: gate.legal_chain.status,
          reasonCode: gate.legal_chain.reason_code,
          code: gate.legal_chain.code,
          versionId: gate.legal_chain.versionId,
          sourceTable: gate.legal_chain.sourceTable,
          blockingReasons: gate.legal_chain.blockingReasons ?? [],
        }
      : undefined,
    versionIds: gate?.version_ids ?? [],
    sourceIds: gate?.source_ids ?? [],
    finalEligibility: gate?.final_eligibility
      ? {
          eligible: gate.final_eligibility.eligible,
          missingProductFacts: gate.final_eligibility.missing_product_facts ?? [],
          auditComplete: gate.final_eligibility.audit_complete,
          blockingReasons: gate.final_eligibility.blocking_reasons ?? [],
        }
      : undefined,
    layers: validation
      ? {
          layer1Passed: validation.layer1_passed,
          layer2Score: validation.layer2_score,
          layer3Passed: validation.layer3_passed,
          method: validation.method,
        }
      : undefined,
  };
}

type ExplainabilityTreeData = Partial<ClassificationResult> & {
  description?: string;
  detected_language?: string;
  detected_intent?: "classification" | "question" | "analysis";
  total_searched?: number;
  embedding_model?: string;
};

interface ExplainabilityTreeWrapperProps {
  data: ExplainabilityTreeData;
  description: string;
}

/**
 * Wrapper that fetches backend-driven explainability if caseId is available,
 * otherwise falls back to reconstructing from classificationData.
 * 
 * V2.1 LEGAL DEFENSIBILITY: Prefer backend data for audit-grade explainability.
 * 
 * REMAND FIX: This wrapper now ONLY uses fields that exist in backend DTO:
 * - case: { caseNumber, direction, classificationDate, inputSnapshot, finalCode, 
 *           resultStatus, confidenceScore, confidenceBand, decidedByType, 
 *           legalSummary, internalSummary }
 * - nomenclatureVersion: { scheme, versionLabel, jurisdiction, validFrom, status }
 * - candidates: [{ code, rank, score, rationale, rejectedReason }]
 * - girSteps: [{ stepOrder, girRuleCode, applied, outcome, rationale, ... }]
 * - legalBasis: [{ sourceType, weight, usage, excerpt }]
 * 
 * Fields that DO NOT exist in backend DTO (and are NOT read):
 * - inputLanguageCode, modeUsed, processingTimeMs, toolsUsed
 */
export function ExplainabilityTreeWrapper({ data, description }: ExplainabilityTreeWrapperProps) {
  const [expanded, setExpanded] = useState(false);

  // V2.1: If we have a caseId, fetch real explainability from backend
  const caseId = data.caseId;
  // NOTE: getCaseExplainability is an action (not a query) — cannot be used with useQuery.
  // Use classificationSpineWriter.getClassificationExplainability directly for reactive frontend consumption.
  const explainabilityQueryRef = api.classificationSpineWriter.getClassificationExplainability;

  const backendExplainability = useQuery(
    explainabilityQueryRef as any,
    (caseId ? { caseId: caseId as Id<"classification_cases"> } : "skip") as any
  ) as Record<string, any> | undefined;

  // C2-A3B: Test-only exposure for authenticated provenance readback smoke.
  // Exposes the actual backendExplainability object to Cypress so the E2E spec
  // can assert on real query results without modifying production auth or queries.
  // No visible UI change. No production user-facing rendering. No secrets.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isCypressRuntime = Boolean((window as any).Cypress);
      const isLocalC2A3BTest =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';

      const isC2A3BObservationEnabled =
        isCypressRuntime ||
        (isLocalC2A3BTest &&
          window.localStorage.getItem('__C2_A3B_TEST_ACTIVE__') === '1');

      if (isC2A3BObservationEnabled) {
        // C2-A3B diagnostic exposure: always set diag object so Cypress can
        // distinguish "no caseId" vs "query not resolved" vs "resolved but empty".
        (window as any).__C2_A3B_EXPLAINABILITY_DIAG__ = {
          caseId: caseId ?? null,
          hasCaseId: Boolean(caseId),
          hasBackendExplainability: Boolean(backendExplainability),
          hasBackendCase: Boolean(backendExplainability?.case),
          legalBasisCount: Array.isArray(backendExplainability?.legalBasis)
            ? backendExplainability.legalBasis.length
            : null,
        };

        if (backendExplainability && backendExplainability.case) {
          (window as any).__C2_A3B_EXPLAINABILITY__ = backendExplainability;
        } else {
          delete (window as any).__C2_A3B_EXPLAINABILITY__;
        }
      }
    }
  }, [backendExplainability, caseId]);

  const normalizeBackendCaseScore = (score: unknown): number => {
    return typeof score === 'number' && Number.isFinite(score)
      ? normalizeConfidenceScore(score)
      : 0.5;
  };

  // Build explainability data from backend or fallback to legacy
  const buildExplainData = (): { explainData: ExplainabilityData; dataSource: 'BACKEND' | 'LEGACY_FALLBACK'; warnings: string[] } => {
    const warnings: string[] = [];

    // If we have backend data, use it (audit-grade, backend-driven)
    if (backendExplainability && backendExplainability.case) {
      const be = backendExplainability;
      const caseData = be.case;

      // REMAND: Extract productDescription from inputSnapshot (the actual backend shape)
      const inputSnapshot = caseData.inputSnapshot as Record<string, unknown> | null | undefined;
      const productDescription = inputSnapshot?.productDescription as string | undefined;

      // R4/R5 REMAND: Track missing data for fail-honest warnings
      if (!be.girSteps || be.girSteps.length === 0) {
        warnings.push("GIR decision steps not recorded");
      }
      if (!be.legalBasis || be.legalBasis.length === 0) {
        warnings.push("Legal basis not recorded");
      }
      if (!be.candidates || be.candidates.length === 0) {
        warnings.push("Classification candidates not recorded");
      }
      
      // REMAND: These fields don't exist in backend DTO — add explicit warnings
      warnings.push("Language detection: not tracked in audit spine");
      warnings.push("Processing time: not tracked in audit spine");
      
      // V2.3: Fail-honest warnings for CN measures completeness
      if (be.measures?.cnMeasures) {
        const cm = be.measures.cnMeasures;
        if (cm.completeness.status === "UNAVAILABLE") {
          warnings.push("⚠️ CN measures: NO DATA available for this HS code");
        } else if (cm.completeness.status === "PARTIAL") {
          warnings.push(`⚠️ CN measures: PARTIAL data — missing: ${cm.completeness.missingData.join(", ")}`);
        }
        if (cm.unresolved.length > 0) {
          warnings.push(`⚠️ ${cm.unresolved.length} measure(s) with unresolved legal consequences`);
        }
        if (cm.conditional.length > 0) {
          warnings.push(`ℹ️ ${cm.conditional.length} measure(s) require certificates/documents to apply`);
        }
      } else if (!be.measures) {
        warnings.push("⚠️ No trade measures data available for legal consequences analysis");
      }
      
      return {
        dataSource: 'BACKEND',
        warnings,
        explainData: {
          input: {
            // REMAND FIX: Use inputSnapshot.productDescription, not caseData.productDescription
            originalDescription: productDescription || description,
            // REMAND FIX: inputLanguageCode doesn't exist in backend — omit or use undefined
            detectedLanguage: undefined, // Will render as "Unknown" in tree
            // REMAND FIX: detectedIntent not in backend — but cases are always classification
            detectedIntent: undefined, // Don't infer
            completenessScore: normalizeBackendCaseScore(caseData.confidenceScore),
          },
          retrieval: {
            sources: [],  // Not tracked in audit spine
            // REMAND FIX: Set to undefined (not -1) to properly hide in UI
            totalSearched: undefined,
            embeddingModel: undefined, // Not tracked
            // REMAND FIX: toolsUsed doesn't exist in backend DTO
            toolsUsed: [],
          },
          legalLogic: {
            girRulesApplied: be.girSteps.map((step: { stepOrder: number; girRuleCode?: string | null; rationale?: string; outcome?: string }) => ({
              rule: step.girRuleCode || `GIR-${step.stepOrder}`,
              explanation: step.rationale || "[Not recorded]",
              decisive: step.outcome === 'ACCEPTED',
            })),
            chapterNotesUsed: be.legalBasis
              .filter((lb: { sourceType?: string }) => lb.sourceType === 'CN_NOTES')
              .map((lb: { excerpt?: string | null }) => lb.excerpt || "[Not recorded]"),
            legalBasis: be.legalBasis.map((lb: { excerpt?: string | null }) => lb.excerpt).filter(Boolean).join('; ') || "[Not recorded]",
            // C2-B1: Preserve full legal basis array with provenance IDs
            legalBasisItems: Array.isArray(be.legalBasis)
              ? be.legalBasis.map((lb: {
                  sourceType: string;
                  weight: number;
                  usage: string;
                  excerpt: string | null;
                  citationId: string | null;
                  chunkId: string | null;
                  documentId: string | null;
                  sourceId: string | null;
                }) => ({
                  sourceType: lb.sourceType,
                  weight: lb.weight,
                  usage: lb.usage,
                  excerpt: lb.excerpt,
                  citationId: lb.citationId,
                  chunkId: lb.chunkId,
                  documentId: lb.documentId,
                  sourceId: lb.sourceId,
                }))
              : undefined,
          },
          decision: {
            hsCode: caseData.finalCode || "N/A",
            description: description,
            confidenceScore: normalizeBackendCaseScore(caseData.confidenceScore),
            confidenceLabel: caseData.confidenceBand || "N/A",
            // REMAND FIX: modeUsed doesn't exist in backend DTO — use undefined
            modeUsed: undefined,
            // REMAND FIX: processingTimeMs doesn't exist in backend DTO — use undefined
            processingTimeMs: undefined,
          },
          reasoning: {
            fullText: caseData.internalSummary || caseData.legalSummary || "[Not recorded]",
            alternativeCodes: be.candidates
              .filter((c: { rank: number }) => c.rank > 1)
              .map((c: { code?: string; rationale?: string | null }) => ({
                code: c.code || "N/A",
                reason: c.rationale || "[Not recorded]",
              })),
          },
          // V2.1 Metadata
          nomenclatureVersion: be.nomenclatureVersion ? {
            code: be.nomenclatureVersion.scheme,
            name: be.nomenclatureVersion.versionLabel,
            effectiveFrom: be.nomenclatureVersion.validFrom,
          } : undefined,
          // V2.2 Measures (duties, restrictions)
          measures: be.measures ? {
            mfnRate: be.measures.mfnRate,
            vatRate: be.measures.vatRate,
            preferentialRates: be.measures.preferentialRates,
            restrictions: be.measures.restrictions,
            prohibitions: be.measures.prohibitions,
            dualUse: be.measures.dualUse,
            hasAnyTradeBarriers: be.measures.hasAnyTradeBarriers,
          } : undefined,          // V2.3: CN Measures with geo area resolution (confirmed/conditional/unresolved)
          cnMeasures: be.measures?.cnMeasures ? {
            confirmed: be.measures.cnMeasures.confirmed,
            conditional: be.measures.cnMeasures.conditional,
            unresolved: be.measures.cnMeasures.unresolved,
            completeness: be.measures.cnMeasures.completeness,
          } : undefined,
          // V2.4: Legal consequences summary
          legalConsequencesSummary: be.measures?.legalConsequencesSummary,
          caseId: caseId,
          // C2-B1: Map backend sourceProvenance when available.
          // Normalize: empty evidenceChain → undefined so the UI does not render
          // a blank Source Provenance accordion.
          sourceProvenance: (() => {
            if (!be.sourceProvenance) return undefined;
            const decisiveSource = be.sourceProvenance.decisiveSource
              ? {
                  type: be.sourceProvenance.decisiveSource.type,
                  sourceType: be.sourceProvenance.decisiveSource.sourceType,
                  sourceId: be.sourceProvenance.decisiveSource.sourceId,
                  authorityLevel: be.sourceProvenance.decisiveSource.authorityLevel,
                  score: be.sourceProvenance.decisiveSource.score ?? 0,
                  reason: be.sourceProvenance.decisiveSource.reason ?? "",
                }
              : undefined;
            const evidenceChain =
              Array.isArray(be.sourceProvenance.evidenceChain) &&
              be.sourceProvenance.evidenceChain.length > 0
                ? be.sourceProvenance.evidenceChain.map((e: {
                    sourceType: "nomenclature" | "legal";
                    identifier: string;
                    role: "AUTHORITATIVE" | "SUPPORTING" | "CORROBORATING";
                    authorityLevel?: "PRIMARY_LEGISLATION" | "SECONDARY_LEGISLATION" | "OFFICIAL_GUIDANCE" | "CASE_LAW" | "INFORMAL_GUIDANCE";
                    contribution: number;
                  }) => ({
                    sourceType: e.sourceType,
                    identifier: e.identifier,
                    role: e.role,
                    authorityLevel: e.authorityLevel,
                    contribution: e.contribution,
                  }))
                : undefined;
            return decisiveSource || evidenceChain
              ? { decisiveSource, evidenceChain }
              : undefined;
          })(),
          // Universal Reasoning Engine output (from classificationPipeline)
          universalReasoning: normalizeUniversalReasoning(data.universalReasoning),
          validationAudit: buildValidationAuditData(data),
        },
      };
    }

    // Fallback: Reconstruct from classification data (LEGACY — NOT audit-grade)
    // R5 REMAND: Mark this clearly as legacy/synthetic
    warnings.push("⚠️ LEGACY FALLBACK: This data is reconstructed from chat response, not from audit spine");
    warnings.push("For audit-grade explainability, ensure classification creates a case ID");
    
    const normalizedConfidenceScore = normalizeConfidenceScore(data.confidence_score);

    return {
      dataSource: 'LEGACY_FALLBACK',
      warnings,
      explainData: {
        input: {
          originalDescription: description || String(data.description || ''),
          // REMAND: Legacy path — use frontend data or undefined
          detectedLanguage: typeof data.detected_language === 'string' ? data.detected_language : undefined,
          detectedIntent: typeof data.detected_intent === 'string' 
            ? (data.detected_intent as 'classification' | 'general_knowledge' | 'conversational')
            : undefined,
          completenessScore: normalizedConfidenceScore,
        },
        retrieval: {
          sources: Array.isArray(data.retrieval_sources) && data.retrieval_sources.length > 0
            ? data.retrieval_sources.map((s) => ({
                id: String(s.id || ''),
                label: String(s.label || 'Izvor'),
                text: String(s.text || ''),
                score: typeof s.score === 'number' ? s.score : undefined,
                origin: String(s.origin || 'chunks'),
              }))
            : [],
          totalSearched: typeof data.total_searched === 'number' ? data.total_searched : undefined,
          embeddingModel: typeof data.embedding_model === 'string' ? data.embedding_model : undefined,
          toolsUsed: Array.isArray(data.tools_used) ? data.tools_used : [],
        },
        legalLogic: {
          girRulesApplied: Array.isArray(data.gir_path)
            ? data.gir_path.map((rule, i) => ({
                rule: `GIR ${i + 1}`,
                explanation: rule,
                decisive: i === 0,
              }))
            : [],
          chapterNotesUsed: [],
          legalBasis: String(data.legal_basis || "[Not recorded]"),
        },
        decision: {
          hsCode: String(data.hs_code || "N/A"),
          description: description || String(data.description || ''),
          confidenceScore: normalizedConfidenceScore,
          confidenceLabel: String(data.confidence || "N/A"),
          modeUsed: (data.mode_used === 'function_calling' || data.mode_used === 'rag')
            ? data.mode_used
            : undefined,
          processingTimeMs: data.processing_time_ms,
        },
        reasoning: {
          fullText: String(data.reasoning || "[Not recorded]"),
          alternativeCodes: Array.isArray(data.alternatives)
            ? data.alternatives.map((a) => ({
                code: String(a.code || "N/A"),
                reason: String(a.reason || "[Not recorded]"),
              }))
            : [],
        },
        // SOURCE PROVENANCE (REMAND FIX: end-to-end evidence chain)
        sourceProvenance: (data.decisiveSource || data.evidenceChain) ? {
          decisiveSource: data.decisiveSource ? {
            type: data.decisiveSource.type,
            sourceType: data.decisiveSource.sourceType,
            sourceId: data.decisiveSource.sourceId,
            authorityLevel: data.decisiveSource.authorityLevel,
            score: data.decisiveSource.score ?? 0,
            reason: data.decisiveSource.reason ?? "",
          } : undefined,
          evidenceChain: Array.isArray(data.evidenceChain) 
            ? data.evidenceChain.map(e => ({
                sourceType: e.sourceType,
                identifier: e.identifier,
                role: e.role,
                authorityLevel: e.authorityLevel,
                contribution: e.contribution,
              }))
            : undefined,
        } : undefined,
        // Universal Reasoning Engine output
        universalReasoning: normalizeUniversalReasoning(data.universalReasoning),
        validationAudit: buildValidationAuditData(data),
      },
    };
  };

  const { explainData, dataSource, warnings } = buildExplainData();

  return (
    <Stack gap="xs" style={{ marginTop: '12px' }}>
      <button
        className="pdf-export-btn"
        onClick={() => setExpanded(!expanded)}
        style={{ marginBottom: expanded ? '8px' : 0 }}
        data-testid="show-chain-of-thought"
      >
        {expanded ? '📊 Sakrij Chain of Thought' : '📊 Prikaži Chain of Thought'}
        {dataSource === 'BACKEND' && <Badge color="green" size="xs" ml="xs">Audit-Grade</Badge>}
        {dataSource === 'LEGACY_FALLBACK' && <Badge color="yellow" size="xs" ml="xs">Legacy</Badge>}
      </button>
      {expanded && (
        <div data-testid="chain-of-thought-panel">
          {/* R5 REMAND: Fail-honest warnings */}
          {warnings.length > 0 && (
            <Alert color={dataSource === 'BACKEND' ? 'yellow' : 'orange'} title="Data Quality Notice" variant="light">
              <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Alert>
          )}
          <ClassificationExplainabilityTree data={explainData} />
        </div>
      )}
    </Stack>
  );
}
